import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Select } from '@/components/ui/Select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { useAuthStore } from '@/stores/authStore';
import { safeString } from '@/components/safeRender';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/formatters';
import { InadimplenciaClientsModal } from '@/components/Dashboard/AgingClientsModal';
import { OperadoresDetalhesModal } from '@/components/Dashboard/OperadoresDetalhesModal';
import { OperadoresGraficoMensal } from '@/components/Dashboard/OperadoresGraficoMensal';
import { KanbanStatsTab } from '@/components/Dashboard/KanbanStatsTab';
import { 
  FileText, 
  AlertTriangle, 
  DollarSign,
  Users,
  Building2,
  UserX,
  BarChart3,
  Calendar,
  Layers
} from 'lucide-react';

export const DashboardPage: React.FC = () => {
  const { user } = useAuthStore();
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<'1-2' | '3-6' | '7+' | null>(null);
  const [operadoresModalOpen, setOperadoresModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('geral');

  // Buscar empresas para o filtro
  const { data: companies } = useQuery({
    queryKey: ['companies', user?.id, user?.role],
    queryFn: async () => {
      const response = await api.get('/companies?page=1&limit=500');
      // Normaliza para sempre retornar um array
      const payload = response?.data?.data ?? response?.data ?? [];
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload?.companies)) return payload.companies;
      if (Array.isArray(payload?.items)) return payload.items;
      return [];
    },
  });

  const { data: dashboardStats, isLoading, error } = useQuery({
    queryKey: ['dashboard-stats', selectedCompany],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedCompany) {
        params.append('companyId', selectedCompany);
      }
      const response = await api.get(`/reports/dashboard?${params.toString()}`);
      return response.data.data;
    },
    retry: 1,
    refetchInterval: 30000, // Atualizar a cada 30 segundos
    refetchIntervalInBackground: true, // Continuar atualizando mesmo com aba inativa
  });

  const { data: kanbanStatsData } = useQuery({
    queryKey: ['kanban-stats', selectedCompany],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedCompany) {
        params.append('companyId', selectedCompany);
      }
      const response = await api.get(`/reports/kanban-stats?${params.toString()}`);
      return response.data.data;
    },
    retry: 1,
    refetchInterval: 30000, // Atualizar a cada 30 segundos
    refetchIntervalInBackground: true, // Continuar atualizando mesmo com aba inativa
  });

  const handleCardClick = (category: '1-2' | '3-6' | '7+') => {
    setSelectedCategory(category);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedCategory(null);
  };

  if (isLoading) {
    return (
      <div className="space-y-6 md:space-y-8" data-testid="page-dashboard">
        <div>
          <h1 className="font-heading text-heading-1 font-bold text-foreground">Dashboard</h1>
          <p className="font-body text-body text-muted-foreground mt-1">
            Bem-vindo, {safeString(user?.name, 'Usuário')}!
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="animate-pulse">
                  <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                  <div className="h-8 bg-muted rounded w-1/2"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 md:space-y-8" data-testid="page-dashboard">
        <div>
          <h1 className="font-heading text-heading-1 font-bold text-foreground">Dashboard</h1>
          <p className="font-body text-body text-muted-foreground mt-1">
            Bem-vindo, {safeString(user?.name, 'Usuário')}!
          </p>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="text-center text-muted-foreground">
              Erro ao carregar estatísticas do dashboard
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const stats = dashboardStats || {
    totalTitulos: 0,
    titulosAbertos: 0,
    titulosVencidos: 0,
    titulosParciais: 0,
    titulosFechados: 0,
    valorTotal: 0,
    operadoresAtivos: 0,
    empresasAtivas: 0,
    clientesInadimplentes: 0,
    mediaTitulosPorCliente: 0,
    aging1a2Dias: { count: 0, valor: 0, porEmpresa: {} },
    aging3a6Dias: { count: 0, valor: 0, porEmpresa: {} },
    agingAcima7Dias: { count: 0, valor: 0, porEmpresa: {} }
  };

  return (
    <div className="space-y-6 md:space-y-8" data-testid="page-dashboard">
      <div>
        <h1 className="font-heading text-heading-1 font-bold text-foreground">Dashboard</h1>
        <p className="font-body text-body text-muted-foreground mt-1">
          Bem-vindo, {safeString(user?.name, 'Usuário')}!
        </p>
        
        {/* Filtro por empresa */}
        <div className="mt-6 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="font-body text-body-sm font-medium text-foreground">Empresa:</label>
            <Select
              value={selectedCompany}
              onValueChange={setSelectedCompany}
              className="w-48"
            >
              <option value="">
                {user?.role === 'ADMIN' ? 'Todas as empresas' : 'Todas no meu escopo'}
              </option>
              {(Array.isArray(companies) ? companies : []).map((company: any) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </Select>
          </div>
          
          {/* Indicador de escopo */}
          <div className="inline-flex items-center px-3 py-1.5 rounded-input font-body text-body-xs font-medium bg-secondary/20 text-secondary">
            <span>
              {user?.role === 'OPERADOR' 
                ? '📊 Estatísticas dos seus títulos' 
                : user?.role === 'GESTOR'
                ? '📊 Estatísticas da sua empresa'
                : '📊 Estatísticas do sistema'
              }
            </span>
          </div>
        </div>
      </div>

      {/* Abas do Dashboard */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="geral" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Visão Geral
          </TabsTrigger>
          <TabsTrigger value="kanban" className="flex items-center gap-2">
            <Layers className="h-4 w-4" />
            Estatísticas por Kanban
          </TabsTrigger>
        </TabsList>

        <TabsContent value="geral" className="space-y-6">
          {/* Cards principais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Títulos</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="font-heading text-heading-2 font-bold text-foreground">{stats.totalTitulos}</div>
            <p className="text-xs text-muted-foreground">
              Títulos no sistema
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Clientes Inadimplentes</CardTitle>
            <UserX className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="font-heading text-heading-2 font-bold text-foreground">{stats.clientesInadimplentes}</div>
            <p className="text-xs text-muted-foreground">
              Com títulos vencidos
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Títulos Vencidos</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="font-heading text-heading-2 font-bold text-foreground">{stats.titulosVencidos}</div>
            <p className="text-xs text-muted-foreground">
              Requerem atenção
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Valor Total</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="font-heading text-heading-2 font-bold text-foreground">{formatCurrency(stats.valorTotal)}</div>
            <p className="text-xs text-muted-foreground">
              Valor em títulos
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Cards secundários */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Média de Títulos por Cliente</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="font-heading text-heading-2 font-bold text-foreground">{Number(stats.mediaTitulosPorCliente ?? 0).toFixed(1)}</div>
            <p className="text-xs text-muted-foreground">
              Títulos por cliente
            </p>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => setOperadoresModalOpen(true)}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Operadores Ativos</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="font-heading text-heading-2 font-bold text-foreground">{stats.operadoresAtivos}</div>
            <p className="text-xs text-muted-foreground">
              Equipe disponível
            </p>
            <p className="text-xs text-blue-600 mt-1">
              Clique para ver detalhes
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Empresas Ativas</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="font-heading text-heading-2 font-bold text-foreground">{stats.empresasAtivas}</div>
            <p className="text-xs text-muted-foreground">
              Clientes ativos
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Cards de Aging */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => handleCardClick('1-2')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">1 a 2 Dias Vencidos</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="font-heading text-heading-2 font-bold text-foreground">{stats.aging1a2Dias?.count || 0}</div>
            <p className="text-xs text-muted-foreground">
              {formatCurrency(stats.aging1a2Dias?.valor || 0)} • {stats.aging1a2Dias?.count || 0} títulos
            </p>
            <p className="text-xs text-blue-600 font-medium">
              {Object.values(stats.aging1a2Dias?.porEmpresa || {}).reduce((sum: number, data: any) => sum + (data.clientesDistintos || 0), 0)} clientes distintos
            </p>
            {stats.aging1a2Dias?.porEmpresa && Object.keys(stats.aging1a2Dias.porEmpresa).length > 0 && (
              <div className="mt-2 space-y-1">
                {Object.entries(stats.aging1a2Dias.porEmpresa).map(([empresa, data]: [string, any]) => (
                  <div key={empresa} className="text-xs text-muted-foreground">
                    {empresa}: {data.count} títulos • {formatCurrency(data.valor)} • {data.clientesDistintos || 0} clientes
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-blue-500 font-medium mt-2">Clique para ver detalhes</p>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => handleCardClick('3-6')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">3 a 6 Dias Vencidos</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="font-heading text-heading-2 font-bold text-foreground">{stats.aging3a6Dias?.count || 0}</div>
            <p className="text-xs text-muted-foreground">
              {formatCurrency(stats.aging3a6Dias?.valor || 0)} • {stats.aging3a6Dias?.count || 0} títulos
            </p>
            <p className="text-xs text-blue-600 font-medium">
              {Object.values(stats.aging3a6Dias?.porEmpresa || {}).reduce((sum: number, data: any) => sum + (data.clientesDistintos || 0), 0)} clientes distintos
            </p>
            {stats.aging3a6Dias?.porEmpresa && Object.keys(stats.aging3a6Dias.porEmpresa).length > 0 && (
              <div className="mt-2 space-y-1">
                {Object.entries(stats.aging3a6Dias.porEmpresa).map(([empresa, data]: [string, any]) => (
                  <div key={empresa} className="text-xs text-muted-foreground">
                    {empresa}: {data.count} títulos • {formatCurrency(data.valor)} • {data.clientesDistintos || 0} clientes
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-blue-500 font-medium mt-2">Clique para ver detalhes</p>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => handleCardClick('7+')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Acima de 7 Dias</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="font-heading text-heading-2 font-bold text-foreground">{stats.agingAcima7Dias?.count || 0}</div>
            <p className="text-xs text-muted-foreground">
              {formatCurrency(stats.agingAcima7Dias?.valor || 0)} • {stats.agingAcima7Dias?.count || 0} títulos
            </p>
            <p className="text-xs text-blue-600 font-medium">
              {Object.values(stats.agingAcima7Dias?.porEmpresa || {}).reduce((sum: number, data: any) => sum + (data.clientesDistintos || 0), 0)} clientes distintos
            </p>
            {stats.agingAcima7Dias?.porEmpresa && Object.keys(stats.agingAcima7Dias.porEmpresa).length > 0 && (
              <div className="mt-2 space-y-1">
                {Object.entries(stats.agingAcima7Dias.porEmpresa).map(([empresa, data]: [string, any]) => (
                  <div key={empresa} className="text-xs text-muted-foreground">
                    {empresa}: {data.count} títulos • {formatCurrency(data.valor)} • {data.clientesDistintos || 0} clientes
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-blue-500 font-medium mt-2">Clique para ver detalhes</p>
          </CardContent>
        </Card>
      </div>

      {/* Resumo por Empresa - Usando os mesmos cálculos dos cards */}
      {dashboardStats?.statsPorEmpresa && dashboardStats.statsPorEmpresa.length > 0 && (
        <div className="space-y-4">
          <div>
            <h2 className="font-heading text-heading-2 font-semibold text-foreground mb-4">Resumo por Empresa</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Estatísticas usando os mesmos cálculos dos cards principais (títulos vencidos + parciais)
            </p>
          </div>

          {/* Totais Gerais */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Totais Gerais</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{stats.totalTitulos}</div>
                  <div className="text-sm text-muted-foreground">Total de Títulos</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">{stats.titulosVencidos}</div>
                  <div className="text-sm text-muted-foreground">Títulos Vencidos</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{formatCurrency(stats.valorTotal)}</div>
                  <div className="text-sm text-muted-foreground">Valor Total</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">{stats.clientesInadimplentes}</div>
                  <div className="text-sm text-muted-foreground">Clientes Inadimplentes</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Estatísticas por Empresa */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Detalhamento por Empresa</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {dashboardStats.statsPorEmpresa.map((empresa: any) => (
                  <div key={empresa.empresa} className="border rounded-lg p-4">
                    <div className="mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-3">
                        {empresa.empresa}
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                          <div className="text-sm text-muted-foreground mb-1">Total de Títulos</div>
                          <div className="text-xl font-bold text-blue-600">{empresa.totalTitulos}</div>
                        </div>
                        <div>
                          <div className="text-sm text-muted-foreground mb-1">Títulos Vencidos</div>
                          <div className="text-xl font-bold text-red-600">{empresa.titulosVencidos}</div>
                        </div>
                        <div>
                          <div className="text-sm text-muted-foreground mb-1">Valor Total</div>
                          <div className="text-xl font-bold text-green-600">{formatCurrency(empresa.valorTotal)}</div>
                        </div>
                        <div>
                          <div className="text-sm text-muted-foreground mb-1">Clientes Inadimplentes</div>
                          <div className="text-xl font-bold text-purple-600">{empresa.clientesInadimplentes}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

          {/* Atividade dos Operadores por Kanban */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Atividade dos Operadores por Kanban</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {kanbanStatsData?.kanbans?.map((kanban: any) => (
                  <div key={kanban.id} className="border rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-4">
                      <div 
                        className="w-4 h-4 rounded-full" 
                        style={{ backgroundColor: kanban.cor }}
                      />
                      <h3 className="text-lg font-semibold">{kanban.nome}</h3>
                      <span className="text-sm text-muted-foreground">
                        ({kanban.operadoresAtividade?.length || 0} operadores)
                      </span>
                    </div>
                    
                    {kanban.operadoresAtividade?.length === 0 ? (
                      <p className="text-muted-foreground text-center py-4">
                        Nenhum operador ativo neste kanban.
                      </p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {kanban.operadoresAtividade?.map((operador: any) => (
                          <div key={operador.id} className="p-3 bg-gray-50 dark:bg-slate-800 rounded">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-medium text-sm">{operador.nome}</h4>
                              <div className="text-xs text-muted-foreground">
                                {operador.titulosAtivos} títulos
                              </div>
                            </div>
                            <div className="space-y-1">
                              <div className="flex justify-between text-xs">
                                <span className="text-muted-foreground">Valor:</span>
                                <span className="font-medium">{formatCurrency(operador.valorTotal)}</span>
                              </div>
                              <div className="flex justify-between text-xs">
                                <span className="text-muted-foreground">Email:</span>
                                <span className="font-medium text-xs">{operador.email}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Gráfico de Atividade dos Operadores */}
          <div className="mt-8">
            <OperadoresGraficoMensal selectedCompany={selectedCompany} />
          </div>
        </TabsContent>

        <TabsContent value="kanban" className="space-y-6">
          <KanbanStatsTab 
            filters={{
              companyId: selectedCompany || undefined,
            }}
          />
        </TabsContent>
      </Tabs>

      {/* Modal de Clientes por Categoria de Aging */}
      <InadimplenciaClientsModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        category={selectedCategory}
        selectedCompany={selectedCompany}
      />

      {/* Modal de Detalhamento dos Operadores */}
      <OperadoresDetalhesModal
        isOpen={operadoresModalOpen}
        onClose={() => setOperadoresModalOpen(false)}
        selectedCompany={selectedCompany}
      />

    </div>
  );
};
